import { Edit, Refresh } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { IconButton, Stack, Typography } from "@mui/material";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import { OnlineMerchantManagement } from "components/OnlineMerchant/OnlineMerchantManagement";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useColors from "utils/useColors/useColors";
import StatusWrapper from "components/StatusWrapper/StatusWrapper";
import useDialogManager from "utils/useDialogManager/useDialogManager";
import useFormUndo from "utils/useFormUndo/useFormUndo";
import usePageState from "utils/usePageState";
import usePageWrapper from "utils/usePageWrapper/usePageWrapper";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";
import { deepEqual, formPreventEnterSubmit } from "utils/utils";

const parseStatusReasonOptions = (obj) => {
  const options = Object.entries(obj).map(([key, value]) => ({
    key,
    title: value,
    value: key
  }))
  options.push({
    key: "OTHERS",
    title: "Others",
    value: "OTHERS"
  })
  return options
}

const OnlineEmployeeDetail = ({ path, redirectFn }) => {
  const { t: tLocal } = useTranslation("translation", { keyPrefix: "page.merchantManagement.onlineEmployeeDetail" });
  const { t: tCommon } = useTranslation();
  const c = useColors();
  const pageState = usePageState({ pagePath: path });
  const { snackbar, snackbarError, snackbarSuccess } = useSnackbarMAS();
  const formProps = useForm({
    mode: "onChange",
  });
  const { handleSubmit } = formProps;
  const { memorizedFormValue, rememberForm, undoForm } = useFormUndo({ formProps });
  const { handleApi, PageWrapper, PageOuterContainer, PageStickyHeader, PageStickyFooter } = usePageWrapper();

  const eidRef = useRef(null);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [isReloading, setIsReloading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const [employeeName, setEmployeeName] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [suspensionRemarks, setSuspensionRemarks] = useState({});


  const employeeId = pageState.employeeId;
  const employeeStatus = useWatch({ control: formProps.control, name: "status" });
  const isLoading = isFirstLoading || isReloading;

  useEffect(() => {
    if (employeeId == "close") {
      redirectFn("close", { tabId: path });
    } else {
      if (eidRef.current !== employeeId) {
        // special handling to prevent multiple initialization (might occur when page is refreshed)
        eidRef.current = employeeId;
        fetchData({ isInit: true });
      }
    }
  }, [employeeId]);

  /**
 * @param {Object} props
 * @param {boolean} props.isInit - pass true if initialization
 * @param {boolean} props.showError - pass true if show error
 * @returns {Promise<boolean>} - true if success
 */
  const fetchData = async ({ isInit, showError } = {}) => {
    isInit && setIsFirstLoading(true);
    setIsReloading(true);

    if (isInit) {
      const [merchantSuspensionRemarks] = await Promise.all([
        OnlineMerchantManagement.Api.getAccountSuspensionRemarks()
      ]);

      if (!handleApi(merchantSuspensionRemarks)) {
        snackbarError({ message: tLocal("toast.suspensionRemarks.failed") });
        return false;
      }

      const suspensionRemarks = merchantSuspensionRemarks.data.data;
      setSuspensionRemarks({
        terminated: suspensionRemarks["TERMINATED"],
        suspended: suspensionRemarks["SUSPENDED"],
      })
    }

    const [employeeDetail] = await Promise.all([
      OnlineMerchantManagement.Api.getEmployee({ id: employeeId })
    ]);

    if (!handleApi(employeeDetail)) {
      (isInit || showError) && snackbarError({ message: tLocal("toast.getDetail.failed") });
      return false;
    }

    const formData = OnlineMerchantManagement.PayloadParser.employeeDetail(employeeDetail.data.data);

    formProps.reset(formData);
    rememberForm(formData);
    setEmployeeName(formData.name);
    setLastUpdated(dayjs(formData.updateTime).format("YYYY-MM-DD HH:mm:ss"));

    isInit && setIsFirstLoading(false);
    setIsReloading(false);

    return true;
  }

  const onSubmit = async (formValues) => {
    if (deepEqual(memorizedFormValue, formValues)) {
      snackbar({ message: tCommon("component.form.undo.identical"), variant: "info" });
      return;
    };

    setIsReloading(true);

    // const test = true;
    // console.log("formValues", formValues);
    // if (test) {
    //   console.log("update employee payload", OnlineMerchantManagement.PayloadBuilder.updateEmployeeDetail({ id: employeeId, ...formValues }));
    //   return;
    // }

    const [updateEmployeeResult] = await Promise.all([
      OnlineMerchantManagement.Api.updateEmployee(OnlineMerchantManagement.PayloadBuilder.updateEmployeeDetail({ id: employeeId, ...formValues }))
    ])

    let isAllApiCallSuccess = true;

    if (!handleApi(updateEmployeeResult)) {
      isAllApiCallSuccess = false;
      snackbarError({ message: tLocal("toast.updateDetail.failed") });
    }

    if (isAllApiCallSuccess) {
      snackbarSuccess({ message: tLocal("toast.updateDetail.success") });
      await fetchData({ showError: true });
      setIsEditing(false);
    }
    setIsReloading(false);
  }

  const statusDialogFormProps = useForm({
    mode: "onChange",
    defaultValues: {
      status: employeeStatus,
      lockAccountType: formProps.getValues("lockAccountType"),
      remark: formProps.getValues("remark")
    }
  });
  const { dialog, openDialog } = useDialogManager({
    title: tCommon("form.onlineMerchantManagement.components.accountStatusDialog.title"),
    getContent: ({ disabled, loading }) => <OnlineMerchantManagement.Components.StatusDialog
      currentStatus={{
        status: employeeStatus,
        lockAccountType: formProps.getValues("lockAccountType"),
        remark: formProps.getValues("remark")
      }}
      formProps={statusDialogFormProps}
      suspendedOptions={parseStatusReasonOptions(suspensionRemarks.suspended ?? {})}
      terminatedOptions={parseStatusReasonOptions(suspensionRemarks.terminated ?? {})}
      loading={loading}
      disabled={disabled}
    />,
    onPositiveAction: async () => {
      if (!(await statusDialogFormProps.trigger())) {
        return false;
      }
      const data = statusDialogFormProps.getValues();
      const result = await OnlineMerchantManagement.Api.updateEmployee(OnlineMerchantManagement.PayloadBuilder.updateEmployeeStatus({ id: employeeId, ...data }, suspensionRemarks));
      if (!handleApi(result)) {
        snackbarError({ message: tLocal("toast.updateStatus.failed") });
      } else {
        snackbarSuccess({ message: tLocal("toast.updateStatus.success") });
      }
      fetchData({ isInit: true });
      return true;
    },
    onOpen: () => {
      statusDialogFormProps.reset();
      return true;
    },
    config: {
      positiveActionText: tCommon("component.simple.dialog.action.3"),
      negativeActionText: tCommon("component.simple.dialog.action.4"),
    }
  })

  return (
    <PageOuterContainer>
      {dialog}
      <PageStickyHeader>
        <Typography variant="h2">{tLocal("label.employeeName", { name: employeeName || "-" })}</Typography>
        <Stack direction="row" justifyContent="space-between">
          <Typography>{tLocal("label.id", { id: employeeId })}</Typography>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography>{tLocal("label.status")}:</Typography>
            <StatusWrapper
              size="small"
              status={
                ["ACTIVE", "SUSPENDED", "TERMINATED"].includes(employeeStatus)
                  ? employeeStatus
                  : "———"
              }
            />
            {employeeStatus !== "TERMINATED" && <IconButton
              size="small"
              onClick={() => {
                openDialog();
              }}
              disabled={isLoading || isEditing}
            >
              <Edit fontSize="small" />
            </IconButton>}
          </Stack>
        </Stack>
      </PageStickyHeader>
      <PageWrapper noPadding isLoading={isFirstLoading}>
        <form noValidate onSubmit={handleSubmit(onSubmit)} onKeyDown={formPreventEnterSubmit}>
          <FormBuilder.Section>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
              {employeeStatus === "ACTIVE" ? <div /> : <OnlineMerchantManagement.Components.AccountStatusAlert
                status={employeeStatus}
                remark={formProps.getValues("remark")}
                date={formProps.getValues("terminatedDate")}
              />}
              <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={2}>
                <Typography variant="content-s" color={c["blackDark999"]}>{tLocal("label.lastUpdated", { time: lastUpdated })}</Typography>
                <LoadingButton
                  key="reload"
                  variant="outline-normal-button"
                  type="button"
                  onClick={() => fetchData()}
                  startIcon={<Refresh />}
                  loading={isReloading}
                  disabled={isEditing}
                >
                  {tCommon("common.refresh")}
                </LoadingButton>
              </Stack>
            </Stack>

            <OnlineMerchantManagement.EmployeeForm.InfoSection
              formProps={formProps}
              isEditing={isEditing}
              isLoading={isLoading}
            />
          </FormBuilder.Section>
          <PageStickyFooter>
            <Stack
              direction="row"
              justifyContent="flex-end"
              gap={2}
            >
              {!isEditing ? (
                <LoadingButton
                  key="edit"
                  variant="normal-button"
                  type="button"
                  onClick={() => {
                    setIsEditing(true)
                    rememberForm();
                  }}
                  disabled={isLoading}
                >
                  {tCommon("common.edit")}
                </LoadingButton>
              ) : (
                <>
                  <LoadingButton
                    key="cancel"
                    variant="outline-normal-button"
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      undoForm();
                    }}
                    disabled={isLoading}
                  >
                    {tCommon("common.cancel")}
                  </LoadingButton>
                  <LoadingButton
                    key="submit"
                    variant="normal-button"
                    type="submit"
                    loading={isLoading}
                  >
                    {tCommon("common.submit")}
                  </LoadingButton>
                </>)}
            </Stack>
          </PageStickyFooter>
        </form>
      </PageWrapper>
    </PageOuterContainer >
  )
};

export default React.memo(OnlineEmployeeDetail);
